import type { SeoLink } from '~/types/school'

// ── Helpers ───────────────────────────────────────────────
/**
 * Parse une chaîne JSON avec repli silencieux en cas d'erreur ou de valeur vide.
 * @template T Type attendu après parsing.
 * @param {string | null | undefined} raw Chaîne JSON ou valeur absente.
 * @param {T} fallback Valeur retournée si `raw` est vide ou si le JSON est invalide.
 * @returns {T} Objet parsé ou `fallback`.
 */
export function parseBlock<T>(raw: string | null | undefined, fallback: T): T {
  if (!raw) return fallback
  try {
    return JSON.parse(raw) as T
  } catch {
    return fallback
  }
}

/**
 * Garde uniquement les liens SEO avec un libellé affichable.
 * @param {SeoLink[] | undefined} links Liste de liens.
 * @returns {SeoLink[]} Liens dont `Text` est non vide après trim.
 */
export function filterSeoLinksWithLabel(links: SeoLink[] | undefined): SeoLink[] {
  if (!links?.length) return []
  return links.filter((link: SeoLink) => (link.Text ?? '').trim().length > 0)
}

/**
 * Retire les caractères de contrôle parasites renvoyés par l'API (ex. `\r`, séquences littérales `\r`).
 * @param {string} text - Texte brut.
 * @returns {string} Texte nettoyé.
 */
export function sanitizeApiText(text: string): string {
  return text
    .replace(/\uFEFF|\u200B/g, '')
    .replace(/\\r\\n/g, '\n')
    .replace(/\\r/g, '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
}

/**
 * Nettoie récursivement toutes les chaînes d'un payload JSON API.
 * @template T
 * @param {T} value - Valeur racine (objet, tableau ou scalaire).
 * @returns {T} Même structure avec les chaînes assainies.
 */
export function sanitizeApiPayload<T>(value: T): T {
  if (typeof value === 'string') {
    return sanitizeApiText(value) as T
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeApiPayload(item)) as T
  }
  if (value !== null && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
      result[key] = sanitizeApiPayload(nested)
    }
    return result as T
  }
  return value
}

/**
 * Garantit qu'une URL possède un schéma absolu et pas de slash final.
 * - Ajoute `https://` si aucun schéma n'est présent (sauf localhost → http).
 * - Retire le `/` final pour éviter les doubles slashes au moment de concaténer les paths.
 * @param {string} raw  La valeur brute issue de la config (peut être vide, sans schéma, avec trailing slash).
 * @returns {string}  Une URL absolue normalisée, ou chaîne vide si l'entrée est vide.
 */
export function normalizeBaseUrl(raw: string | undefined | null): string {
  const value = raw?.trim()
  if (!value) return ''

  // Déjà un schéma explicite (http://, https://, etc.) → on respecte le choix.
  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(value)

  // localhost / 127.0.0.1 sans schéma → http (le dev local n'est pas en TLS).
  const isLocal = /^(localhost|127\.0\.0\.1)(:\d+)?/i.test(value)

  const withScheme = hasScheme ? value : `${isLocal ? 'http' : 'https'}://${value}`

  // Retire un éventuel slash final unique.
  return withScheme.replace(/\/+$/, '')
}

/**
 * Retire le HTML d'une chaîne API et renvoie le texte brut (ex. H1 avec lien).
 * @param {string} text - Chaîne brute (souvent `h1` SEO).
 * @returns {string} Libellé sans balises, prêt pour un affichage texte (`{{ }}`).
 */
export function removeHtmlToString(text: string): string {
  return sanitizeApiText(text)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<a\b[^>]*>(.*?)<\/a>/gi, '$1')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Prépare le HTML d'introduction SEO pour `v-html`.
 * @param {string} text - Texte brut API (`introduction`).
 * @returns {string} Chaîne avec retours convertis en `<br>`.
 */
export function formatSeoIntro(text: string): string {
  return sanitizeApiText(text)
    .replace(/\r<br>/gi, '<br>')
    .replace(/\r/g, '')
    .replace(/\n/g, '<br>')
}

/**
 * @param {string} fileOrUrl - Fichier `public/` ou URL http(s).
 * @param {string} defaultFileName - Nom de fichier par défaut sous `public/`.
 * @returns {string} Chemin public (`/fichier.json`) ou URL absolue.
 */
function resolvePublicFallbackPath(fileOrUrl: string, defaultFileName: string): string {
  const trimmed: string = fileOrUrl.trim()
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  const fileName: string = trimmed.replace(/^\//, '') || defaultFileName
  return fileName.startsWith('/') ? fileName : `/${fileName}`
}

/**
 * Charge un JSON fallback : URL absolue (env) → lecture `public/` (SSR / prerender) → `$fetch` static (client).
 * Évite le `$fetch` HTTP vers soi-même au build (prerender CI).
 * @template T
 * @param {string} fileOrUrl - Fichier `public/` ou URL http(s) (`NUXT_PUBLIC_*_FALLBACK_URL`).
 * @param {string} defaultFileName - Nom de fichier par défaut.
 * @param {string} requestOrigin - Origin courante (`useRequestURL().origin`).
 * @returns {Promise<T | null>} Payload JSON ou `null`.
 */
export async function loadFallbackJsonPayload<T>(
  fileOrUrl: string,
  defaultFileName: string,
  requestOrigin: string,
): Promise<T | null> {
  const target: string = resolvePublicFallbackPath(fileOrUrl, defaultFileName)

  if (/^https?:\/\//i.test(target)) {
    try {
      const payload: unknown = await $fetch(target, { timeout: 8000 })
      return payload as T
    } catch {
      return null
    }
  }

  if (import.meta.server) {
    const { readPublicFallbackJson } = await import('~/utils/loadFallbackJson.server')
    return readPublicFallbackJson<T>(target)
  }

  try {
    const payload: unknown = await $fetch(`${requestOrigin}${target}`, { timeout: 8000 })
    return payload as T
  } catch {
    return null
  }
}
